/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { CursorManager } from '../../../core/navigation';
import { GuideCell } from '../../../core/entities/GuideCell';
import { Channel } from '../../../core/entities/Channel';
import { Log } from '../../../core/logging/Log';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { AppUtils } from '../../../core/AppUtils';
import {
    guideHideSpinner,
    loadGridGuideNextChannels,
    loadGridGuidePreviousChannels,
} from '../guide.reducers';
import {
    setHorizontalPosition,
    setVerticalPosition,
} from '../guidehighlight/guidehighlight.reducers';
import { onLeftMainGuideOperations, onRightMainGuideOperations } from './mainguide.reducers';
import { onLeftPastGuideOperations, onRightPastGuideOperations } from './pastguide.reducers';
import { showChannelOptionsOverlay } from '../../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import { showExitConfirmationOverlayDialog } from '../../overlay/exitconfirmationoverlay/exitconfirmationoverlay.reducers';
import { setVerticalAndHorizontalPositionAction } from '../guidehighlight/guidehighlight.actions';
import { showNoNetworkOverlay } from '../../overlay/nonetworkoverlay/nonetworkoverlay.reducers';
import {
    setEnteredChannelNumber,
    startEnterChannelNumber,
} from '../enterchannelnumber/enterchannelnumber.reducers';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { VuLogger } from '../../../core/logging/VULogger';
import { GridGuideRow } from '../../../core/entities/GridGuideRow';
import { IDLE_GRID_GUIDE_DURATION_IN_SECOND } from '../../../config';
import { showRecordOverlay } from '../../overlay/recordoverlay/recordoverlay.reducers';
import { videoPlayerStartPlaybackAction } from '../../videoplayer/videoplayer.actions';
import { SocuDataModelImpl } from '../../../core/socu/impls/SocuDataModelImpl';
import { VideoManager } from '../../../core/videoplayer';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { UniqueIdentifier } from '../../../core/utils';

const TAG = 'guidekeyoperations';

const checkProgramAndInvokeRecordOverlay =
    (showMetaData?: boolean) => (dispatch: Function, getState: Function) => {
        const { gridGuide, guideHighlight } = getState();
        const { channels, guideRows, isChannelsServiceFail } = gridGuide;
        const { verticalPosition, horizontalPosition } = guideHighlight;

        if (isChannelsServiceFail) return;

        if (NetworkManager.isNetworkDisconnected()) {
            dispatch(showNoNetworkOverlay());
            return;
        }

        if (horizontalPosition) {
            const guideCells: GuideCell[] = guideRows[verticalPosition]?.guideCells;
            if (guideCells?.length) {
                // horizontalPosition has 1 additional position of channel, -1 to get program index
                const guideCell: GuideCell = guideCells[horizontalPosition - 1];

                const channel: Channel = channels[verticalPosition];
                dispatch(showRecordOverlay({ channel, guideCell, showMetaData }));
            }
        }
    };

export const cancelGuideSpinnerOperations = () => (dispatch: Function) => {
    UniqueIdentifier.getNextActionsNetworkCallId();
    UniqueIdentifier.getNextOttActionDetailsNetworkCallId();
    dispatch(guideHideSpinner());
};

export const onKeyRightGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, guideHighlight } = getState();
    const {
        isPastGuideMode,
        isChannelsServiceFail,
        isGuideRowsServiceFail,
        currentTimestamp,
        guideRows,
        futureGuideRows,
        pastGuideRows,
    } = gridGuide;
    const { horizontalPosition } = guideHighlight;

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );

    if (isChannelsServiceFail || !hasValidGuideRows) return;

    if (isGuideRowsServiceFail && horizontalPosition === 1) return;

    if (isPastGuideMode) {
        dispatch(onRightPastGuideOperations());
    } else {
        dispatch(onRightMainGuideOperations());
    }
};

export const onKeyLeftGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { guideHighlight, gridGuide } = getState();
    const {
        isPastGuideMode,
        isChannelsServiceFail,
        isGuideRowsServiceFail,
        currentTimestamp,
        pastGuideRows,
        guideRows,
        futureGuideRows,
    } = gridGuide;

    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );
    const { horizontalPosition } = guideHighlight;

    if (isChannelsServiceFail || !hasValidGuideRows) return;

    if (isGuideRowsServiceFail && horizontalPosition === 0) return;

    if (!horizontalPosition) dispatch(setHorizontalPosition(0));

    if (isPastGuideMode) {
        dispatch(onLeftPastGuideOperations());
    } else {
        dispatch(onLeftMainGuideOperations());
    }
};

export const onKeyDownGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, horizontalPosition, horizontalSession } = getState().guideHighlight;
    const {
        LIMIT,
        guideRows,
        currentTimestamp,
        channels,
        isChannelsServiceFail,
        futureGuideRows,
        pastGuideRows,
    } = getState().gridGuide;
    const nextVerticalPosition = verticalPosition + 1;
    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );

    if (isChannelsServiceFail) return;
    if (horizontalPosition > 0 && !hasValidGuideRows) return;
    let loadNextChannels: boolean = false;
    if (channels && channels.length !== 0 && horizontalPosition === 0) {
        if (nextVerticalPosition === LIMIT) {
            loadNextChannels = true;
        } else {
            dispatch(setVerticalPosition(nextVerticalPosition));
        }
    } else if (guideRows && guideRows.length !== 0 && guideRows[verticalPosition + 1]) {
        const { guideCells } = guideRows[verticalPosition + 1];
        const currentIndex = evaluateCurrentHorizontalSessionIndex(
            guideCells,
            currentTimestamp,
            horizontalSession,
        );
        const nextHorizontalPosition = guideCells && guideCells.length === 1 ? 1 : currentIndex + 1;
        dispatch(
            setVerticalAndHorizontalPositionAction(nextVerticalPosition, nextHorizontalPosition),
        );
    } else {
        loadNextChannels = true;
    }

    if (loadNextChannels) {
        dispatch(loadGridGuideNextChannels());
    }
};

export const onKeyUpGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, horizontalPosition, horizontalSession } = getState().guideHighlight;
    const {
        guideRows,
        currentTimestamp,
        channels,
        isChannelsServiceFail,
        futureGuideRows,
        pastGuideRows,
    } = getState().gridGuide;
    const previousVerticalPosition = verticalPosition - 1;
    const hasValidGuideRows: boolean = isValidGuideRows(
        pastGuideRows,
        guideRows,
        futureGuideRows,
        currentTimestamp,
    );
    if (isChannelsServiceFail) return;
    if (horizontalPosition > 0 && !hasValidGuideRows) return;
    let loadPreviousChannels: boolean = false;
    if (channels && channels.length !== 0 && horizontalPosition === 0) {
        if (previousVerticalPosition < 0) {
            loadPreviousChannels = true;
        } else {
            dispatch(setVerticalPosition(previousVerticalPosition));
        }
    } else if (guideRows && guideRows.length !== 0 && guideRows[verticalPosition - 1]) {
        const { guideCells } = guideRows[verticalPosition - 1];
        const currentIndex = evaluateCurrentHorizontalSessionIndex(
            guideCells,
            currentTimestamp,
            horizontalSession,
        );
        const nextHorizontalPosition = guideCells && guideCells.length === 1 ? 1 : currentIndex + 1;
        dispatch(
            setVerticalAndHorizontalPositionAction(
                previousVerticalPosition,
                nextHorizontalPosition,
            ),
        );
    } else {
        loadPreviousChannels = true;
    }

    if (loadPreviousChannels) {
        dispatch(loadGridGuidePreviousChannels());
    }
};

export const onKeyOkGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, guideHighlight } = getState();
    const { channels, guideRows, isChannelsServiceFail, offerPreview } = gridGuide;
    const { verticalPosition, horizontalPosition } = guideHighlight;

    if (isChannelsServiceFail) return;

    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showNoNetworkOverlay());
        return;
    }

    if (horizontalPosition === 0) {
        dispatch(showChannelOptionsOverlay());
        Log.w(TAG, 'Channel options overlay will be called here.');
    } else {
        const guideCells: GuideCell[] = guideRows[verticalPosition]?.guideCells;
        if (guideCells?.length > 0) {
            const currentTime = new Date();
            const channel: Channel = channels[verticalPosition];
            // Current-Horizontal-Position 0 means "Focus is on channel cell"
            const currentHorizontalPosition = horizontalPosition === 0 ? 0 : horizontalPosition - 1;
            const guideCell: GuideCell = guideCells[currentHorizontalPosition];
            const { startTime, duration } = guideCell;
            const endTime = AppUtils.addSecondsToDateTime(startTime, duration);

            const isCurrentlyAiring = currentTime >= startTime && currentTime < endTime;
            dispatch(videoPlayerStartPlaybackAction(channel));
            if (isCurrentlyAiring) {
                const payload: Payload = VuLogger.createPayload(guideCell, channel, offerPreview);
                VU.itemSelectedEvent(payload);
                const socuModelImpl = SocuDataModelImpl.getInstance();
                if (socuModelImpl.isSocu()) {
                    VideoManager.getInstance().updateLastViewedPosition();
                    socuModelImpl.resetSocuData();
                }
                dispatch(startVideoPlayback(channel));
                if (channel.channelNumber !== QoEAppLogger.lastTunedChannel) {
                    QoEAppLogger.setWatchContentEndReason(
                        QoEAppLogger.PlaybackEndReason
                            .USER_MOVE_TO_LIVE_TV_FROM_GUIDE_AND_CHANNEL_WAS_CHANGED,
                    );
                }
                if (
                    channel.channelNumber !==
                    VideoManager.getInstance().getPlayable()?.channel?.channelNumber
                ) {
                    VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
                }
            } else {
                dispatch(showRecordOverlay({ channel, guideCell }));
            }
        }
    }

    Log.i(TAG, `OK_KEY`);
    Log.i(TAG, channels[verticalPosition]);
};

export const onKeyPlayGuideOperations = () => (dispatch: Function) => {
    if (CursorManager.isCursorAppeared()) return;
    dispatch(onKeyOkGuideOperations());
};

export const onLongKeyOkGuideOperations = () => (dispatch: Function) => {
    dispatch(checkProgramAndInvokeRecordOverlay());
};

export const onKeyBackGuideOperations = () => (dispatch: Function) => {
    dispatch(showExitConfirmationOverlayDialog());
};

export const onKeyInfoGuideOperations = () => (dispatch: Function) => {
    dispatch(checkProgramAndInvokeRecordOverlay(true));
};

export const onKeyDigitGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { isEnterChannelNumberActive, enteredChannelNumber } = getState().enterChannelNumber;

    if (!isEnterChannelNumberActive) dispatch(startEnterChannelNumber());
    dispatch(setEnteredChannelNumber());

    Log.i(TAG, enteredChannelNumber);
};

export const onKeyDashGuideOperations = () => (dispatch: Function, getState: Function) => {
    const { isEnterChannelNumberActive } = getState().enterChannelNumber;
    if (!isEnterChannelNumberActive) {
        dispatch(startEnterChannelNumber());
    }
};

export const evaluateCurrentHorizontalSessionIndex = (
    guideCells: GuideCell[],
    currentTimestamp,
    horizontalSession,
): number => {
    return guideCells?.findIndex((guideCell: GuideCell) => {
        const { startTime, duration } = guideCell;
        const _startTime =
            startTime?.getTime() < currentTimestamp.getTime() ? currentTimestamp : startTime;
        const _endTime = AppUtils.addSecondsToDateTime(startTime, duration);

        return (
            horizontalSession.getTime() >= _startTime?.getTime() &&
            horizontalSession.getTime() < _endTime?.getTime()
        );
    });
};

const isValidGuideRows = (
    pastGuideRows: GridGuideRow[],
    guideRows: GridGuideRow[],
    futureGuideRows: GridGuideRow[],
    currentTimestamp: Date,
): boolean => {
    const hasNotValidGuideRows: boolean =
        !guideRows ||
        (guideRows && guideRows.length === 0) ||
        !futureGuideRows ||
        (futureGuideRows && futureGuideRows.length === 0) ||
        !pastGuideRows ||
        (pastGuideRows && pastGuideRows.length === 0);
    if (hasNotValidGuideRows) return !hasNotValidGuideRows;

    const { startTime, duration } = guideRows[0].guideCells[0];
    const ProgramEndTime = AppUtils.addSecondsToDateTime(startTime, duration);
    const guideEndTime = AppUtils.addSecondsToDateTime(
        currentTimestamp,
        IDLE_GRID_GUIDE_DURATION_IN_SECOND,
    );

    if (
        startTime.getTime() === currentTimestamp.getTime() ||
        ProgramEndTime.getTime() <= guideEndTime.getTime() ||
        (startTime.getTime() < currentTimestamp.getTime() &&
            ProgramEndTime.getTime() >= guideEndTime.getTime())
    )
        return true;
    return false;
};
